import { RatingStars } from "@/components/community/rating-stars"
import { formatDate } from "@/lib/utils"
import type { Review } from "@/types/review"

import { RecipeReviewForm } from "./recipe-review-form"

type RecipeReviewsProps = {
  recipeId: string
  initialReviews: Review[]
}

function calculateSummary(reviews: Review[]) {
  const count = reviews.length

  if (count === 0) {
    return {
      average: 0,
      count: 0,
    }
  }

  const total = reviews.reduce((sum, review) => sum + review.rating, 0)
  const average = Math.round((total / count) * 10) / 10

  return { average, count }
}

function getReviewWord(count: number) {
  const mod10 = count % 10
  const mod100 = count % 100

  if (mod10 === 1 && mod100 !== 11) {
    return "відгуку"
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return "відгуків"
  }

  return "відгуків"
}

export function RecipeReviews({
  recipeId,
  initialReviews,
}: RecipeReviewsProps) {
  const summary = calculateSummary(initialReviews)

  return (
    <section id="reviews" className="space-y-6">
      <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-6">
          <div className="eyebrow">Відгуки читачів</div>

          <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
            Що пишуть про рецепт
          </h2>

          <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
            Тут відображаються відгуки користувачів, які вже пройшли модерацію.
            Вони допомагають краще зрозуміти, як рецепт працює в домашніх умовах.
          </p>
        </div>

        {summary.count > 0 ? (
          <div className="mb-8 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-3xl font-semibold tracking-tight text-neutral-950">
                  {summary.average.toFixed(1)}
                </div>
                <div className="mt-1 text-sm text-neutral-600">
                  На основі {summary.count} {getReviewWord(summary.count)}
                </div>
              </div>

              <RatingStars value={summary.average} size="lg" />
            </div>
          </div>
        ) : (
          <div className="mb-8 rounded-[1.5rem] border border-dashed border-neutral-300 bg-neutral-50 p-5 text-sm leading-7 text-neutral-600">
            Поки що немає опублікованих відгуків. Якщо ви вже готували цей
            рецепт, можете залишити свій відгук нижче.
          </div>
        )}

        <div className="space-y-4">
          {initialReviews.length > 0 ? (
            initialReviews.map((review) => (
              <article
                key={review.id}
                className="rounded-[1.5rem] border border-neutral-200 bg-white p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-neutral-950">
                      {review.authorName}
                    </h3>
                    <div className="mt-1 text-sm text-neutral-500">
                      {formatDate(review.createdAt)}
                    </div>
                  </div>

                  <RatingStars value={review.rating} />
                </div>

                <p className="mt-4 text-sm leading-7 text-neutral-700 sm:text-base">
                  {review.text}
                </p>
              </article>
            ))
          ) : null}
        </div>
      </div>

      <RecipeReviewForm recipeId={recipeId} />
    </section>
  )
}